import { LineString } from "geojson";

import { Raster } from "@nens/lizard/api-v4";
import {
  FileDescriptorRead,
  OGC3DTilesetsMeta,
  RasterMeta,
  RasterUserSuppliedMeta,
  ScenarioMeta,
  SchematisationMeta,
  SchematisationUserSuppliedMeta,
  VectorMeta,
  VectorUserSuppliedMeta,
} from "@nens/rana/api-v1";
import { objectEntries, objectFromEntries, objectKeys } from "@nens/shared-utils";

import { FileDataTypeEnum } from "@/lib/rana-file-data-type";

// DESCRIPTORS

export type ScenarioLayer = {
  id: string | null;
  code: string;
  name: string;
  label: string | null;
};

type ValidFileDescriptorStatus = NonNullable<FileDescriptorRead["status"]> & { id: "completed" };

type OverwriteFileDescriptorRead = Omit<
  FileDescriptorRead,
  "meta" | "userSuppliedMeta" | "status"
> & {
  status: ValidFileDescriptorStatus;
};

export type RasterFileDescriptor = OverwriteFileDescriptorRead & {
  meta: RasterMeta;
  userSuppliedMeta: RasterUserSuppliedMeta;
};

export type SchematisationFileDescriptor = OverwriteFileDescriptorRead & {
  meta: SchematisationMeta;
  userSuppliedMeta: SchematisationUserSuppliedMeta;
};

export type VectorFileDescriptor = OverwriteFileDescriptorRead & {
  meta: VectorMeta;
  userSuppliedMeta: VectorUserSuppliedMeta;
};

export type ScenarioFileDescriptor = OverwriteFileDescriptorRead & {
  meta: ScenarioMeta;
};

export type OgcFileDescriptor = OverwriteFileDescriptorRead & {
  meta: OGC3DTilesetsMeta;
};

// I should never be exported
const descriptorTypeMap = {
  [FileDataTypeEnum.Raster]: {} as RasterFileDescriptor,
  [FileDataTypeEnum.Schematisation]: {} as SchematisationFileDescriptor,
  [FileDataTypeEnum.Vector]: {} as VectorFileDescriptor,
  [FileDataTypeEnum.Scenario]: {} as ScenarioFileDescriptor,
  [FileDataTypeEnum.Ogc]: {} as OgcFileDescriptor,
};

export const descriptorTypeKeys = objectKeys(descriptorTypeMap);
export type FileDescriptorDataTypesMap = typeof descriptorTypeMap;
export type FileDescriptorDataType = keyof FileDescriptorDataTypesMap;

// DESCRIPTOR VIEWS

export type ThreediModel = {
  id: number;
  extent_zero_d: LineString | null;
  extent_one_d: LineString | null;
  extent_two_d: LineString | null;
};

export type SchematisationRaster = {
  url: string;
  type: string;
  name: string;
  file: {
    url: string;
    filename: string;
    state: string;
    state_description: string;
    type: string;
    size: number;
    etag: string;
    expiry_date: Date;
    id: number;
    meta: unknown | null;
  };
  id: number;
  epsg_code: number;
  extent: LineString;
  geotransform: [number, number, number, number, number, number];
  unit?: string | null;
};

export type Schematisation = {
  management_url: string;
  schematisation: {
    id: number;
    name: string;
    slug: string;
    created_by: string;
    created_by_first_name: string;
    created_by_last_name: string;
    created: Date;
    last_updated?: Date | null;
  };
  latest_revision?: {
    id: number;
    has_threedimodel: boolean;
    threedimodel?: ThreediModel | null;
    rasters?: SchematisationRaster[] | null;
    number: number;
  } | null;
};

// Currently supported view types for file descriptors (there are more in the backend. This is just the ones we actively use)
// I should never be exported
const viewTypeRegistry = {
  "lizard-raster": {
    dataTypes: [FileDataTypeEnum.Raster],
    args: undefined,
    value: {} as Raster,
  },
  "threedi-schematisation": {
    dataTypes: [FileDataTypeEnum.Schematisation],
    args: undefined,
    value: {} as Schematisation,
  },
  pmtiles: {
    dataTypes: [FileDataTypeEnum.Schematisation, FileDataTypeEnum.Vector],
    args: undefined,
    value: {} as { urls: Record<string, string> },
  },
  "gpkg-table": {
    dataTypes: [FileDataTypeEnum.Vector],
    args: {} as { layer_id: string; table_id: string },
    value: {} as { [featureId: string]: [number, number][] },
  },
} satisfies Record<string, { dataTypes: FileDescriptorDataType[]; args: unknown; value: unknown }>;

export type ViewToDataTypeMap = {
  [V in keyof typeof viewTypeRegistry]: (typeof viewTypeRegistry)[V]["dataTypes"];
};

export const viewToDataTypeMap = objectFromEntries(
  objectEntries(viewTypeRegistry).map(([viewType, { dataTypes }]) => [viewType, dataTypes])
) as ViewToDataTypeMap;

export type DescriptorViewType = keyof typeof viewTypeRegistry;

export type ViewTypesArgs = {
  [V in DescriptorViewType]: (typeof viewTypeRegistry)[V]["args"];
};

export type ViewTypesMap = {
  [V in DescriptorViewType]: (typeof viewTypeRegistry)[V]["value"];
};
